import { useNuxtApp } from '#app'
import { isTrue } from '@/utils/siteConfigChecker'

function getContext() {
  if (!import.meta.client) return null

  const nuxtApp = useNuxtApp()
  if (!nuxtApp._customerServiceContext) {
    nuxtApp._customerServiceContext = {
      initializedServices: new Set(),
      livechatService: null,
      tawkService: null,
      salesmartlyService: null,
      cxgenieService: null,
    }
  }
  return nuxtApp._customerServiceContext
}

function setUp({ csChat = [], locale = 'en' }) {
  if (!import.meta.client) return

  const ctx = getContext()
  if (!ctx) return

  csChat.forEach((item) => {
    if (!isTrue(item.enable) || ctx.initializedServices.has(item.name)) {
      return
    }

    // Livechat
    if (item.name === 'livechat') {
      if (window.__lc?.license === item.id) return
      window.__lc = window.__lc || {}
      window.__lc.license = item.id
      window.__lc.group = item.group
      ;(function (n, t, c) {
        function i(n) {
          return e._h ? e._h.apply(null, n) : e._q.push(n)
        }
        var e = {
          _q: [],
          _h: null,
          _v: '2.0',
          on() {
            i(['on', c.call(arguments)])
          },
          once() {
            i(['once', c.call(arguments)])
          },
          off() {
            i(['off', c.call(arguments)])
          },
          get() {
            if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.")
            return i(['get', c.call(arguments)])
          },
          call() {
            i(['call', c.call(arguments)])
          },
          init() {
            const n = t.createElement('script')
            ;((n.async = !0), (n.type = 'text/javascript'), (n.src = 'https://cdn.livechatinc.com/tracking.js'), t.head.appendChild(n))
          },
        }
        ;(!n.__lc.asyncInit && e.init(), (n.LiveChatWidget = n.LiveChatWidget || e))
      })(window, document, [].slice)

      let loaded = false
      let callback = null

      function liveCall(action, param) {
        if (loaded) {
          livechatClient.call(action, param)
        } else {
          callback = function () {
            livechatClient.call(action, param)
          }
        }
      }

      const livechatClient = window.LiveChatWidget || undefined
      livechatClient.on('ready', () => {
        loaded = true
        if (callback) {
          callback()
          callback = null
        }
      })

      ctx.livechatService = {
        maximize() {
          try {
            liveCall('maximize')
          } catch (error) {
            console.log('Livechat not ready yet')
          }
        },
        minimize() {
          try {
            liveCall('minimize')
          } catch (error) {
            console.log('Livechat not ready yet')
          }
        },
        hide() {
          try {
            liveCall('hide')
          } catch (error) {
            console.log('Livechat not ready yet')
          }
        },
        setUser(uidSite) {
          liveCall('set_customer_name', uidSite)
        },
      }
    }

    // Twak
    if (item.name === 'tawk') {
      if (window.Tawk_API) return
      var Tawk_API = Tawk_API || {},
        Tawk_LoadStart = new Date()
      ;(function (window, document) {
        var s1 = document.createElement('script'),
          s0 = document.getElementsByTagName('script')[0]
        s1.async = true
        s1.src = item.id
        s1.charset = 'UTF-8'
        s1.setAttribute('crossorigin', '*')
        s1.onerror = function () {
          console.warn('[Customer Service] Tawk script failed to load')
        }
        s0.parentNode.insertBefore(s1, s0)
        window.Tawk_API = Tawk_API // inject到window上，若不需要使用API的操作方法，可以不用
      })(window, document)

      let loaded = false
      let callback = null

      function TawkCall(action, param) {
        const executeAction = () => {
          try {
            switch (action) {
              case 'maximize':
                Tawk_API.maximize()
                break
              case 'minimize':
                Tawk_API.minimize()
                break
              case 'hide':
                Tawk_API.minimize()
                break
              case 'setUser':
                Tawk_API.setAttributes({ name: param }, function (error) {
                  if (error) {
                    console.warn('[Customer Service] Tawk setAttributes error:', error)
                  }
                })
                break
              default:
                console.log('Unknown Tawk_API Action')
                break
            }
          } catch (e) {
            console.warn('[Customer Service] Tawk action error:', e)
          }
        }

        if (loaded) {
          executeAction()
        } else {
          callback = executeAction
        }
      }

      Tawk_API.onLoad = function () {
        loaded = true
        const currentCtx = getContext()
        if (currentCtx?.tawkService) {
          currentCtx.tawkService.loaded = true
        }
        console.log(`[Customer Service] Tawk Ready`)
        try {
          Tawk_API.hideWidget()
        } catch (e) {
          console.warn('[Customer Service] Tawk hideWidget error:', e)
        }
        if (callback) {
          try {
            callback()
          } catch (e) {
            console.warn('[Customer Service] Tawk callback error:', e)
          }
          callback = null
        }
      }

      ctx.tawkService = {
        loaded: false,
        maximize() {
          try {
            TawkCall('maximize')
          } catch (error) {
            console.log('Tawk not ready yet')
          }
        },
        minimize() {
          try {
            TawkCall('minimize')
          } catch (error) {
            console.log('Tawk not ready yet')
          }
        },
        hide() {
          try {
            TawkCall('hide')
          } catch (error) {
            console.log('Tawk not ready yet')
          }
        },
        setUser(uidSite) {
          try {
            TawkCall('setUser', uidSite)
          } catch (error) {
            console.log('Tawk not ready yet')
          }
        },
      }
    }

    // SaleSmartly Chat
    if (item.name === 'salesmartly') {
      const getSaleSmartlyApi = () => {
        if (window.ssq && typeof window.ssq.push === 'function') {
          return window.ssq
        }
        ;(function (d, s, id, w, n) {
          w.__ssc = w.__ssc || {}
          w.__ssc.license = item.id
          w.__ssc.setting = { hideIcon: true }
          if (w.ssq) return
          n = w.ssq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
          }
          n.push = n
          n.loaded = !0
          n.queue = []
          const sjs = d.getElementsByTagName(s)[0]
          if (d.getElementById(id)) return
          const js = d.createElement(s)
          js.id = id
          js.src = 'https://assets.salesmartly.com/chat/widget/code/install.js'
          sjs.parentNode.insertBefore(js, sjs)
        })(document, 'script', 'ss-chat', window)
        return window.ssq
      }

      const SALESMARTLY_API = getSaleSmartlyApi()
      if (!SALESMARTLY_API || typeof SALESMARTLY_API.push !== 'function') {
        console.warn('[Customer Service] SaleSmartly API 初始化失敗')
        return
      }

      let loaded = false
      const pending = []

      const saleSmartlyCall = (action, param) => {
        if (loaded) {
          SALESMARTLY_API.push(action, param)
        } else {
          pending.push([action, param])
        }
      }

      SALESMARTLY_API.push('onReady', () => {
        loaded = true
        console.log('[Customer Service] SaleSmartly Ready')
        while (pending.length) {
          const [action, param] = pending.shift()
          SALESMARTLY_API.push(action, param)
        }
      })

      ctx.salesmartlyService = {
        maximize() {
          try {
            saleSmartlyCall('chatOpen')
          } catch (error) {
            console.log('SaleSmartly not ready yet')
          }
        },
        minimize() {
          try {
            saleSmartlyCall('chatClose')
          } catch (error) {
            console.log('SaleSmartly not ready yet')
          }
        },
        hide() {
          try {
            saleSmartlyCall('chatClose')
          } catch (error) {
            console.log('SaleSmartly not ready yet')
          }
        },
        setUser(uidSite) {
          saleSmartlyCall('setLoginInfo', {
            user_id: uidSite,
            user_name: uidSite,
          })
        },
      }
    }

    // CX Genie AI
    if (item.name === 'cxgenie') {
      const cxgenieScript = document.createElement('script')
      cxgenieScript.src = item.link
      cxgenieScript.setAttribute('data-aid', item.id)
      cxgenieScript.setAttribute('data-lang', locale)
      cxgenieScript.setAttribute('data-hide-widget-button', 'true') // 預設隱藏 widget 按鈕
      document.head.appendChild(cxgenieScript)

      let loaded = false
      let callback = null

      function cxGenieCall(action, param) {
        if (loaded) {
          switch (action) {
            case 'maximize':
              window.openCXGenieChatWidget()
              break
            case 'minimize':
              window.closeCXGenieChatWidget()
              break
            case 'hide':
              window.closeCXGenieChatWidget()
              break
            case 'setUser':
              console.log('CX Genie Can not set user.')
              break
            default:
              console.log('Unknown CX Genie Action')
              break
          }
        } else {
          callback = function () {
            cxGenieCall(action, param)
          }
        }
      }

      cxgenieScript.onload = () => {
        loaded = true
        console.log('[Customer Service] CX Genie Ready')
        if (callback) {
          callback()
          callback = null
        }
      }

      ctx.cxgenieService = {
        maximize() {
          try {
            cxGenieCall('maximize')
          } catch (error) {
            console.log('CX Genie not ready yet')
          }
        },
        minimize() {
          try {
            cxGenieCall('minimize')
          } catch (error) {
            console.log('CX Genie not ready yet')
          }
        },
        hide() {
          try {
            cxGenieCall('hide')
          } catch (error) {
            console.log('CX Genie not ready yet')
          }
        },
        setUser(uidSite) {
          try {
            cxGenieCall('setUser', { user_id: uidSite, user_name: uidSite })
          } catch (error) {
            console.log('CX Genie not ready yet')
          }
        },
      }
    }

    ctx.initializedServices.add(item.name)
  })
}

function doAction(csItem, action, param) {
  if (!import.meta.client) return

  const ctx = getContext()
  if (!ctx) return

  const { name, link } = csItem || {}
  switch (name) {
    case 'livechat':
      if (ctx.livechatService) {
        ctx.livechatService[action]?.call(ctx.livechatService, param)
      }
      break
    case 'tawk':
      if (ctx.tawkService?.loaded) {
        ctx.tawkService[action].call(ctx.tawkService, param)
      }
      break
    case 'salesmartly':
      if (ctx.salesmartlyService) {
        ctx.salesmartlyService[action]?.call(ctx.salesmartlyService, param)
      }
      break
    case 'cxgenie':
      if (ctx.cxgenieService) {
        ctx.cxgenieService[action]?.call(ctx.cxgenieService, param)
      }
      break
    case 'telegram':
      if (action === 'maximize') {
        window.open(link)
      }
      break
    case 'line':
      if (action === 'maximize') {
        window.open(link)
      }
      break
    case 'all':
      if (action === 'minimize' || action === 'hide') {
        if (ctx.livechatService) {
          ctx.livechatService[action].call(ctx.livechatService)
        }
        if (ctx.tawkService) {
          ctx.tawkService[action].call(ctx.tawkService)
        }
        if (ctx.salesmartlyService) {
          ctx.salesmartlyService[action].call(ctx.salesmartlyService)
        }
        if (ctx.cxgenieService) {
          ctx.cxgenieService[action].call(ctx.cxgenieService)
        }
      }
      break
    default:
      console.log('Unknown customer service !!', name)
      break
  }
}

export default { setUp, doAction }
